// Canonical payment-term day options — must mirror ClientInvoice.payment_terms_days
// choices on the backend. Change in both places.
export const PAYMENT_TERM_DAY_OPTIONS = [7, 14, 21, 30, 45, 60, 90] as const;

export interface MonthlyPayrollActual {
  id: string;
  company: string;
  company_name: string;
  year: number;
  month: number;
  net_uop: string;
  net_uz: string;
  net_b2b: string;
  zus: string;
  pit: string;
  total: string;
  notes: string;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TheoreticalEmployee {
  employee_id: string;
  employee_name: string;
  monthly_cost: string;
  effective_from: string;
  effective_to: string | null;
}

export interface TheoreticalCompany {
  company: string;
  company_short_code: string;
  year: number;
  month: number;
  theoretical_total: string;
  employees: TheoreticalEmployee[];
}

export type PaymentStatus = "unpaid" | "partially_paid" | "paid";
export type ClientInvoiceStatus = "draft" | "issued";
export type ClientInvoiceType = "regular" | "proforma" | "proforma_advance";

export const INVOICE_TYPE_LABELS: Record<ClientInvoiceType, string> = {
  regular: "Faktura PRZ",
  proforma: "Proforma",
  proforma_advance: "Proforma zaliczkowa",
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: "Nieopłacona",
  partially_paid: "Częściowo opłacona",
  paid: "Opłacona",
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, "gray" | "yellow" | "green"> = {
  unpaid: "gray",
  partially_paid: "yellow",
  paid: "green",
};

export const CLIENT_INVOICE_STATUS_LABELS: Record<ClientInvoiceStatus, string> = {
  draft: "Szkic",
  issued: "Wystawiona",
};

export const CLIENT_INVOICE_STATUS_COLORS: Record<ClientInvoiceStatus, "gray" | "blue"> = {
  draft: "gray",
  issued: "blue",
};

export type ClientInvoiceEventType =
  | "created"
  | "status_changed"
  | "file_uploaded"
  | "sent_to_investor"
  | "payment_updated"
  | "awaiting_accountant"
  | "expected_date_changed"
  | "invoice_date_changed"
  | "line_item_changed"
  | "comment"
  | "proforma_converted"
  | "footer_changed";

export const CLIENT_INVOICE_EVENT_LABELS: Record<ClientInvoiceEventType, string> = {
  created: "Utworzono",
  status_changed: "Zmiana statusu",
  file_uploaded: "Plik faktury",
  sent_to_investor: "Wysłano do inwestora",
  payment_updated: "Aktualizacja płatności",
  awaiting_accountant: "Czeka na księgowego",
  expected_date_changed: "Termin płatności",
  invoice_date_changed: "Data wystawienia",
  line_item_changed: "Pozycje faktury",
  comment: "Komentarz",
  proforma_converted: "Przekształcono w FV VAT",
  footer_changed: "Stopka faktury",
};

export interface ClientInvoiceEvent {
  id: string;
  event_type: ClientInvoiceEventType;
  description: string;
  amount: string | null;
  user_name: string;
  created_at: string;
  file_details: ManagedFileDetail[];
}

export interface ManagedFileDetail {
  id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  url: string | null;
}

/**
 * Wynik analizy AI pliku faktury PRZ. Backend zwraca to pole tym, którzy mają dostęp
 * do finansów tej faktury — u pozostałych jest `null`, więc panel się nie renderuje.
 */
export interface ClientInvoiceAiLineItem {
  name: string;
  quantity: string;
  unit_value: string;
  total: string;
}

export interface ClientInvoiceAiAnalysis {
  status: "pending" | "running" | "ok" | "warning" | "error";
  status_label?: string;
  expected_seller?: string;
  expected_buyer?: string;
  expected_buyer_nip?: string;
  expected_net?: string | null;
  expected_payment_term_days?: number | null;
  expected_line_items?: ClientInvoiceAiLineItem[];
  document_readable?: boolean;
  seller_found?: string;
  seller_ok?: boolean | null;
  buyer_found?: string;
  buyer_nip_found?: string;
  buyer_ok?: boolean | null;
  net_amount_found?: string | null;
  net_amount_ok?: boolean | null;
  payment_term_days_found?: number | null;
  payment_term_ok?: boolean | null;
  line_items_found?: ClientInvoiceAiLineItem[];
  line_items_total_found?: string | null;
  line_items_ok?: boolean | null;
  line_items_note?: string;
  summary?: string;
  error_message?: string;
  model_used?: string;
  analyzed_at?: string | null;
}

export interface ClientInvoice {
  id: string;
  project: string;
  project_name: string;
  company_name: string | null;
  client: string | null;
  client_name: string | null;
  client_nip_resolved: string | null;
  client_krs: string | null;
  client_regon: string | null;
  client_address: string | null;
  invoice_number: string;
  invoice_managed_file: string | null;
  invoice_managed_file_detail: ManagedFileDetail | null;
  amount_net: string;
  amount_gross: string;
  invoice_date: string;
  fv_vat_date: string | null;
  payment_terms_days: number;
  payment_due_date: string | null;
  description: string;
  footer_text: string;
  status: ClientInvoiceStatus;
  director_approved: boolean | null;
  is_advance_invoice: boolean;
  invoice_type: ClientInvoiceType;
  is_proforma: boolean;
  awaiting_accountant: boolean;
  sent_to_investor: boolean;
  sent_to_investor_at: string | null;
  payment_status: PaymentStatus;
  paid_amount: string;
  payment_date: string | null;
  payment_comment: string;
  expected_payment_date: string | null;
  planned_revenue_id: string | null;
  planned_revenue_description: string | null;
  planned_revenue_amount: string | null;
  planned_revenue_expected_date: string | null;
  line_items_count: number;
  /** Tylko przy dostępie do finansów tej faktury — u reszty null. */
  ai_analysis: ClientInvoiceAiAnalysis | null;
  // Consortium / joint venture
  consortium_agreement: string | null;
  consortium_agreement_detail: ConsortiumAgreementBrief | null;
  is_consortium: boolean;
  jv_partner_net: string;
  jv_partner_gross: string;
  our_net_share: string;
  our_gross_share: string;
  jv_invoices: JointVentureInvoice[];
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface ConsortiumAgreementBrief {
  id: string;
  name: string;
  partner_name: string;
  our_share_percent: string;
  is_active: boolean;
  partner_nip: string;
  partner_krs: string;
  partner_regon: string;
  partner_address: string;
  partner_bank_account: string;
  partner_bank_name: string;
}

export interface ConsortiumAgreement extends ConsortiumAgreementBrief {
  notes: string;
  invoices_count: number;
  /** Pełna wartość netto faktur oznaczonych tą umową. */
  invoices_net_total: string;
  /** Nasza część netto — z dopiętych FV partnera, a gdy ich brak, wg udziału %. */
  our_net_total: string;
  partner_net_total: string;
  /** Ile faktur ma dopięte FV partnera (realny podział zamiast umownego %). */
  attached_partner_invoices: number;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export type JointVentureInvoiceStatus = "draft" | "pending" | "approved" | "transfer_prepared" | "rejected" | "paid";

export interface JointVentureInvoice {
  id: string;
  client_invoice: string;
  consortium_agreement: string | null;
  consortium_agreement_detail: ConsortiumAgreementBrief | null;
  partner_name: string;
  resolved_partner_name: string;
  invoice_number: string;
  amount_net: string;
  amount_gross: string;
  invoice_date: string | null;
  managed_file: string | null;
  managed_file_detail: ManagedFileDetail | null;
  status: JointVentureInvoiceStatus;
  status_display: string;
  is_booked: boolean;
  paid_at: string | null;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  notes: string;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface ClientInvoiceLineItem {
  id: string;
  position: number;
  name: string;
  unit_value: string;
  quantity: string;
  total: string;
}

export interface ClientInvoiceFormData {
  project?: string;
  company?: string;
  client?: string;
  invoice_number: string;
  amount_net: string;
  amount_gross: string;
  invoice_date: string;
  payment_terms_days?: number;
  description?: string;
  footer_text?: string;
  status?: ClientInvoiceStatus;
  is_advance_invoice?: boolean;
  invoice_type?: ClientInvoiceType;
  awaiting_accountant?: boolean;
  sent_to_investor?: boolean;
}

export interface GlobalCostAllocationItem {
  project: string;
  project_name: string;
  project_short_name: string;
  fraction: number;
  is_auto: boolean;
}

export interface GlobalCostAllocationSummary {
  allocations: GlobalCostAllocationItem[];
  unassigned_one_time_total: string;
  unassigned_recurring_monthly: string;
}

export interface CostMatrixProject {
  id: string;
  short_name: string;
  name: string;
  status: "active" | "suspended" | "inactive";
  total_value: string | null;
  weight_pct: number;
  salary_cost: string;
  days_active_pct: number;
}

// Macierz dla global-cost-allocations (podział kosztów ogólnych) — inny kształt niż
// macierz kosztów zleceń niżej (ta operuje na `rows`). Osobne nazwy, by się nie zlewały.
export interface GlobalCostMatrixData {
  year: number;
  month: number;
  projects: CostMatrixProject[];
  total_salary_pool: string;
  total_allocated: string;
}

export interface InternalCostInvoice {
  id: string;
  month: string | null;
  url: string | null;
  original_filename: string;
  uploaded_by_name: string;
  created_at: string;
}

export interface InternalCostOneTime {
  id: string;
  project: string | null;
  project_name: string | null;
  company: string | null;
  company_name: string | null;
  description: string;
  amount: string;
  cost_date: string;
  is_paid: boolean;
  paid_at: string | null;
  source_expense_id: string | null;
  source_expense_employee_name: string | null;
  invoice?: InternalCostInvoice | null;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

/** A recurring cost's monthly-invoice file (faktura comiesięczna), as shown under the cost. */
export interface RecurringCostInvoiceFile {
  id: string;
  amount: string;
  /** Netto w walucie faktury — zasila średnią kosztu; null dla starych wpisów. */
  amount_net: string | null;
  currency: string;
  amount_pln: string | null;
  status: EmployeeExpenseStatus;
  is_booked: boolean;
  period_year: number | null;
  period_month: number | null;
  period_label: string | null;
  employee_name: string | null;
  submitted_by_name: string | null;
  url: string | null;
  original_filename: string | null;
}

export interface InternalCostRecurring {
  id: string;
  project: string | null;
  project_name: string | null;
  company: string | null;
  company_name: string | null;
  description: string;
  /** DEPRECATED — recurring costs no longer carry a fixed amount. */
  amount_monthly: string | null;
  /** Average NET PLN amount of the cost's invoices — the figure fed into forecasts. */
  average_amount: string;
  /** The monthly-invoice files linked to this cost (faktury comiesięczne). */
  expense_invoices: RecurringCostInvoiceFile[];
  /** Months closed WITHOUT an invoice („faktury nie było") — counted as 0,00 zł. */
  month_skips: {
    period_year: number;
    period_month: number;
    period_label: string | null;
    note: string;
  }[];
  day_of_month: number;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface EmployeeSalaryCost {
  id: string;
  employee: string;
  employee_name: string;
  monthly_cost: string;
  effective_from: string;
  effective_to: string | null;
  company: string | null;
  company_name: string | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectEmployeeAllocation {
  id: string;
  employee: string;
  employee_name: string;
  project: string;
  project_name: string;
  year: number;
  month: number;
  fraction: string;
  created_at: string;
  updated_at: string;
}

export interface InternalCostAllocation {
  id: string;
  project: string;
  project_name: string;
  project_short_name: string;
  fraction: string;
  created_at: string;
  updated_at: string;
}

export type EmployeeExpenseType = "invoice" | "mileage" | "proforma";
export type EmployeeExpenseStatus = "pending" | "approved" | "transfer_prepared" | "rejected" | "paid";

export const EXPENSE_STATUS_LABELS: Record<EmployeeExpenseStatus, string> = {
  pending: "Oczekuje",
  approved: "Zaakceptowana",
  transfer_prepared: "Przelew przygotowany",
  rejected: "Odrzucona",
  paid: "Opłacona",
};

export const EXPENSE_STATUS_COLORS: Record<EmployeeExpenseStatus, "gray" | "blue" | "yellow" | "red" | "green"> = {
  pending: "gray",
  approved: "blue",
  transfer_prepared: "yellow",
  rejected: "red",
  paid: "green",
};

export interface EmployeeExpense {
  id: string;
  employee: string;
  employee_name: string;
  submitted_by: string | null;
  submitted_by_name: string | null;
  project: string | null;
  project_name: string | null;
  /** Set when this expense is a monthly-invoice file for a recurring cost (faktura comiesięczna). */
  recurring_cost: string | null;
  recurring_cost_detail: {
    id: string;
    description: string;
    project_name: string | null;
    company_name: string | null;
  } | null;
  period_year: number | null;
  period_month: number | null;
  /** Human label like „Lipiec 2026" (null for non-monthly expenses). */
  period_label: string | null;
  expense_type: EmployeeExpenseType;
  description: string;
  amount: string;
  currency: string;
  amount_pln: string | null;
  is_reimbursement: boolean;
  /** Pro-forma paid up front: normal approve/pay flow, but NOT an accounting cost
   *  (no internal cost, no VAT, no real booking) — the real FV is filed separately. */
  is_proforma: boolean;
  invoice_managed_file: string | null;
  invoice_managed_file_detail: ManagedFileDetail | null;
  status: EmployeeExpenseStatus;
  director_comment: string;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  paid_at: string | null;
  paid_by: string | null;
  paid_by_name: string | null;
  is_booked: boolean;
  booked_at: string | null;
  booked_by: string | null;
  booked_by_name: string | null;
  internal_cost_id: string | null;
  last_undoable_event: {
    id: string;
    event_type_display: string;
    can_undo: boolean;
  } | null;
  created_at: string;
  updated_at: string;
}

export type EmployeeExpenseEventType =
  | "created"
  | "edited"
  | "approved"
  | "rejected"
  | "transfer_prepared"
  | "paid"
  | "booked"
  | "unbooked"
  | "reverted";

export interface EmployeeExpenseEvent {
  id: string;
  /** Source file id — only meaningful in the merged monthly-invoice timeline. */
  expense: string;
  event_type: EmployeeExpenseEventType;
  event_type_display: string;
  actor: string | null;
  actor_name: string | null;
  description: string;
  /** Source file's period („Lipiec 2026") in the shared history; null otherwise. */
  period_label: string | null;
  is_reverted: boolean;
  is_revert: boolean;
  reverted_event: string | null;
  created_at: string;
}


export interface BlockingSubtaskBrief {
  id: string;
  title: string;
  status: string;
  task_id: string;
  task_title: string;
}

export interface PlannedRevenue {
  id: string;
  project: string;
  project_name: string;
  project_short_name: string;
  description: string;
  notes: string;
  amount: string;
  expected_date: string;
  is_auto_generated: boolean;
  blocking_subtasks: BlockingSubtaskBrief[];
  is_blocked: boolean;
  draft_invoice_id: string | null;
  draft_invoice_number: string | null;
  draft_invoice_payment_status: PaymentStatus | null;
  draft_invoice_remaining_net: string | null;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

export type ProjectStatus = "active" | "suspended" | "inactive";

export interface ActivitySegment {
  from_day: number;
  to_day: number;
  status: ProjectStatus;
}

export interface CostMatrixRow {
  project_id: string;
  project_name: string;
  project_short_name: string;
  status: ProjectStatus;
  is_active: boolean;
  days_active_pct: number | null;
  activity_segments: ActivitySegment[] | null;
  salary_cost: string | null;
  other_cost: string;
}

export interface CostMatrixData {
  year: number | null;
  month: number | null;
  rows: CostMatrixRow[];
}

// Sposób liczenia kosztów płac/stałych na przedsięwzięciu (musi zgadzać się z backendem:
// apps/finances/cost_methods.py::CostMethod). PROPORTIONAL = ze smarem wolnych mocy + udziałem
// w globalnych; DIRECT = tylko realne przydziały + własne koszty.
export const COST_METHOD = {
  PROPORTIONAL: "proportional",
  DIRECT: "direct",
} as const;
export type CostMethod = (typeof COST_METHOD)[keyof typeof COST_METHOD];

// Wiersz zwinięty do grupy zleceń (?group_by=group). Drobnica łączy się w jeden wiersz.
export interface CostMatrixGroupRow {
  group_id: string | null;
  group_name: string;
  group_short_name: string;
  is_group: true;
  project_count: number;
  is_active: boolean;
  salary_cost: string | null;
  other_cost: string;
}

export interface GroupedCostMatrixData {
  year: number | null;
  month: number | null;
  grouped: boolean;
  rows: CostMatrixGroupRow[];
}

